import { isAdmin } from '@/constants/roles';
import { isEmbeddedSidebar } from '@/lib/embedded-sidebar';
import { hasMessage } from '@/lib/message';
import { cn } from '@/lib/utils';
import { MutableRefObject, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { FileSpec, useChatMessages } from '@chainlit/react-client';
import { useConfig } from '@chainlit/react-client';

import WaterMark from '@/components/WaterMark';

import { useAuth } from '@/hooks/useAuth';

import ChatDisclaimer from './ChatDisclaimer';
import MessageComposer from './MessageComposer';
import PodcastPlayer from './PodcastPlayer';

interface Props {
    fileSpec: FileSpec;
    onFileUpload: (payload: File[]) => void;
    onFileUploadError: (error: string) => void;
    autoScrollRef: MutableRefObject<boolean>;
    showIfEmptyThread?: boolean;
    composerDisabled?: boolean;
}

export default function ChatFooter({
    showIfEmptyThread,
    composerDisabled,
    ...props
}: Props) {
    const { messages } = useChatMessages();
    const { id: urlThreadId } = useParams<{ id?: string }>();
    const { user } = useAuth();
    const { config } = useConfig();

    const isPodcastAuthorized = useMemo(() => {
        if (!user) return false;
        const userIsAdmin = isAdmin(user?.metadata?.organization_roles);
        const isEnabledForOrg = config?.features?.podcast_enabled !== false;
        return userIsAdmin && isEnabledForOrg;
    }, [user, config]);
    return (
        <div
            className={cn(
                'relative flex flex-col items-center w-full',
                isEmbeddedSidebar ? 'gap-1 px-6 pb-1' : 'gap-2'
            )}
        >
            {(hasMessage(messages) || (showIfEmptyThread && !!urlThreadId)) && (
                <>
                    {isPodcastAuthorized && <PodcastPlayer className="mb-2" />}
                    <MessageComposer {...props} disabled={composerDisabled} />
                </>
            )}
            <ChatDisclaimer />
        </div>
    );
}
